import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";

const ChildWorkplanModal = ({
  show,
  onClose,
  onSuccess,
  facilityId,
  workplan,
  masterWorkplans,
  selectedMasterWorkplanId,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parent: "",
    status: "Pending",
  });

  useEffect(() => {
    if (workplan) {
      setFormData({
        name: workplan.name || "",
        description: workplan.description || "",
        parent: workplan.parent || "",
        status: workplan.status || "Pending",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        parent: selectedMasterWorkplanId || "",
        status: "Pending",
      });
    }
  }, [workplan, selectedMasterWorkplanId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        description: formData.description,
        parent: formData.parent,
        status: formData.status,
      };

      let response;
      if (workplan) {
        response = await makeRequest2(
          `/api/work_plan/child/${facilityId}/${workplan._id}`,
          "PUT",
          submitData,
        );
      } else {
        response = await makeRequest2(
          `/api/work_plan/child/${facilityId}`,
          "POST",
          submitData,
        );
      }

      if (response && (response.success || response.message)) {
        onSuccess();
      } else {
        toastify(response.error || "An error occurred", "error");
      }
    } catch (error) {
      toastify("An error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {workplan ? "Edit Child Task" : "Add Child Task"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Master Workplan *</label>
                    <select
                      className="form-select"
                      name="parent"
                      value={formData.parent}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Master Workplan</option>
                      {masterWorkplans.map((master) => (
                        <option key={master._id} value={master._id}>
                          {master.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Task Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Check fuel levels"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Undone">Undone</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Task Description *</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="6"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe the task in detail...&#10;&#10;You can include:&#10;- Step-by-step instructions&#10;- Safety requirements&#10;- Tools needed&#10;- Expected outcomes"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <div className="alert alert-info">
                      <small>
                        <strong>Tip:</strong> Provide detailed instructions in
                        the description field. This will help staff understand
                        exactly what needs to be done for this task.
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="alert alert-warning">
                    <small>
                      <i className="ti ti-info-circle me-1"></i>
                      <strong>Daily Completion Tracking:</strong> Once created,
                      this task can be marked as completed on a daily basis
                      through the master workplan accordion view. This helps
                      track routine maintenance activities.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !formData.name || !formData.parent}
              >
                {loading && (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                )}
                {workplan ? "Update" : "Create"} Child Task
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChildWorkplanModal;
